/** Scene 2: «Дом (чтение)» — living room with TV at 4 m, laptop at 66 cm, phone at 40 cm (docs/SCENES.md §2). */

import { t, tl, type Lang } from '../i18n';
import {
  ellipseDeg, fillRectDeg, geom, polygonDeg, rectDeg, roundRectPath, setFont, textDeg, wrapText, withShadow,
} from './draw-utils';
import { RENDER_H, RENDER_W, type Scene } from './types';

const HFOV = 40;
const G = geom(HFOV);
const FLOOR_Y = -5;

function drawFar(ctx: CanvasRenderingContext2D, lang: Lang): void {
  // Wall and floor.
  const wall = ctx.createLinearGradient(0, 0, 0, G.Y(FLOOR_Y));
  wall.addColorStop(0, '#f1e8d8');
  wall.addColorStop(1, '#dccbb2');
  ctx.fillStyle = wall;
  ctx.fillRect(0, 0, RENDER_W, RENDER_H);
  const floor = ctx.createLinearGradient(0, G.Y(FLOOR_Y), 0, RENDER_H);
  floor.addColorStop(0, '#b48c5c');
  floor.addColorStop(1, '#8c6a44');
  fillRectDeg(ctx, G, -20, 20, -12.2, FLOOR_Y, floor);
  ctx.strokeStyle = 'rgba(70, 45, 20, 0.25)';
  ctx.lineWidth = 2;
  for (let i = -20; i <= 20; i += 4) {
    ctx.beginPath();
    ctx.moveTo(G.X(i), G.Y(FLOOR_Y));
    ctx.lineTo(G.X(i * 1.6), RENDER_H);
    ctx.stroke();
  }
  // Skirting board.
  fillRectDeg(ctx, G, -20, 20, FLOOR_Y, FLOOR_Y + 0.35, '#efe6d6');

  drawWindow(ctx);
  drawTv(ctx, lang);
  drawClock(ctx);
  drawCalendar(ctx, lang);
}

function drawWindow(ctx: CanvasRenderingContext2D): void {
  const x0 = 5;
  const x1 = 20;
  const y0 = -3;
  const y1 = 12;
  const frame = 0.35;
  withShadow(ctx, 'rgba(60, 40, 20, 0.25)', 18, 6, () => {
    fillRectDeg(ctx, G, x0, x1, y0, y1, '#f6f3ec');
  });
  // Glass: sky, distant building, tree.
  const glass = rectDeg(G, x0 + frame, x1 - frame, y0 + frame, y1 - frame);
  ctx.save();
  ctx.beginPath();
  ctx.rect(glass.x, glass.y, glass.w, glass.h);
  ctx.clip();
  const sky = ctx.createLinearGradient(0, glass.y, 0, glass.y + glass.h);
  sky.addColorStop(0, '#7fb8ea');
  sky.addColorStop(1, '#d4e9f8');
  ctx.fillStyle = sky;
  ctx.fillRect(glass.x, glass.y, glass.w, glass.h);
  // Distant building.
  fillRectDeg(ctx, G, 13.5, 19.7, -2.7, 4.2, '#b9bec6');
  ctx.fillStyle = '#8d97a6';
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const rr = rectDeg(G, 14.1 + c * 1.15, 14.8 + c * 1.15, -2.2 + r * 1.25, -1.5 + r * 1.25);
      ctx.fillRect(rr.x, rr.y, rr.w, rr.h);
    }
  }
  // Tree.
  fillRectDeg(ctx, G, 8.3, 9.1, -2.7, 2.5, '#6b4a2b');
  ctx.fillStyle = '#5c9a45';
  ellipseDeg(ctx, G, 8.7, 4.6, 3.1, 2.7);
  ctx.fill();
  ctx.fillStyle = '#6fae52';
  ellipseDeg(ctx, G, 7.4, 3.4, 1.9, 1.7);
  ctx.fill();
  ellipseDeg(ctx, G, 10.0, 3.2, 1.9, 1.7);
  ctx.fill();
  ctx.restore();
  // Bars.
  fillRectDeg(ctx, G, 12.35, 12.65, y0, y1, '#f6f3ec');
  fillRectDeg(ctx, G, x0, x1, 4.35, 4.65, '#f6f3ec');
  // Sill.
  fillRectDeg(ctx, G, x0 - 0.4, x1 + 0.4, y0 - 0.5, y0, '#e9e3d6');
}

function drawTv(ctx: CanvasRenderingContext2D, lang: Lang): void {
  const x0 = -16;
  const x1 = 1;
  const y0 = 0;
  const y1 = 9.5;
  withShadow(ctx, 'rgba(0, 0, 0, 0.35)', 24, 8, () => {
    fillRectDeg(ctx, G, x0, x1, y0, y1, '#15181c');
  });
  // Stand.
  polygonDeg(ctx, G, [[-9, y0], [-6, y0], [-5.2, -0.9], [-9.8, -0.9]]);
  ctx.fillStyle = '#2a2e33';
  ctx.fill();
  fillRectDeg(ctx, G, -11, -4, -1.1, -0.9, '#2a2e33');
  // Screen.
  const inset = 0.3;
  const s = rectDeg(G, x0 + inset, x1 - inset, y0 + inset, y1 - inset);
  const bg = ctx.createLinearGradient(s.x, s.y, s.x + s.w, s.y + s.h);
  bg.addColorStop(0, '#0d4f86');
  bg.addColorStop(1, '#1f7ac0');
  ctx.fillStyle = bg;
  ctx.fillRect(s.x, s.y, s.w, s.h);
  // Header.
  textDeg(ctx, G, t(lang, 'home.tv.title'), x0 + 0.9, y1 - 1.35, 0.7, '#ffffff', 700);
  // Sun icon.
  ctx.fillStyle = '#ffd35c';
  ellipseDeg(ctx, G, x1 - 1.6, y1 - 1.1, 0.55, 0.55);
  ctx.fill();
  // City rows.
  const cities = tl(lang, 'home.tv.cities');
  cities.forEach((row, i) => {
    const y = y1 - 2.9 - i * 1.25;
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    const rr = rectDeg(G, x0 + 0.8, x1 - 0.8, y - 0.3, y + 0.85);
    ctx.fillRect(rr.x, rr.y, rr.w, rr.h);
    textDeg(ctx, G, row, x0 + 1.1, y, 0.6, '#ffffff', 500);
  });
  // Ticker.
  fillRectDeg(ctx, G, x0 + inset, x1 - inset, y0 + inset, y0 + 1.1, '#0a2f52');
  textDeg(ctx, G, t(lang, 'home.tv.ticker'), x0 + 0.9, y0 + 0.55, 0.38, '#dbe9f7', 400);
}

function drawClock(ctx: CanvasRenderingContext2D): void {
  // Between the TV and the window (DECISIONS D-008).
  const cx = 3.3;
  const cy = 9.6;
  const r = 1.6;
  withShadow(ctx, 'rgba(0,0,0,0.25)', 12, 4, () => {
    ctx.fillStyle = '#2b2f36';
    ellipseDeg(ctx, G, cx, cy, r, r);
    ctx.fill();
  });
  ctx.fillStyle = '#fbfbf7';
  ellipseDeg(ctx, G, cx, cy, r - 0.18, r - 0.18);
  ctx.fill();
  ctx.strokeStyle = '#2b2f36';
  ctx.lineWidth = 2;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const inner = i % 3 === 0 ? r - 0.55 : r - 0.4;
    ctx.beginPath();
    ctx.moveTo(G.X(cx + Math.sin(a) * inner), G.Y(cy + Math.cos(a) * inner));
    ctx.lineTo(G.X(cx + Math.sin(a) * (r - 0.25)), G.Y(cy + Math.cos(a) * (r - 0.25)));
    ctx.stroke();
  }
  const num = (label: string, dx: number, dy: number): void => {
    setFont(ctx, 0.4 * G.ppd, 600);
    ctx.fillStyle = '#2b2f36';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, G.X(cx + dx), G.Y(cy + dy));
  };
  num('12', 0, r - 0.85);
  num('3', r - 0.85, 0);
  num('6', 0, -(r - 0.85));
  num('9', -(r - 0.85), 0);
  ctx.textBaseline = 'alphabetic';
  // Hands at 10:10.
  const hand = (angleDeg: number, len: number, width: number): void => {
    const a = (angleDeg * Math.PI) / 180;
    ctx.strokeStyle = '#1d2026';
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(G.X(cx), G.Y(cy));
    ctx.lineTo(G.X(cx + Math.sin(a) * len), G.Y(cy + Math.cos(a) * len));
    ctx.stroke();
  };
  hand(-60, r * 0.55, 5);
  hand(60, r * 0.8, 3.5);
  ctx.fillStyle = '#1d2026';
  ellipseDeg(ctx, G, cx, cy, 0.12, 0.12);
  ctx.fill();
}

function drawCalendar(ctx: CanvasRenderingContext2D, lang: Lang): void {
  const x0 = 1.9;
  const x1 = 4.7;
  const y0 = 2.2;
  const y1 = 6.2;
  withShadow(ctx, 'rgba(0,0,0,0.2)', 10, 4, () => {
    fillRectDeg(ctx, G, x0, x1, y0, y1, '#fbfaf6');
  });
  fillRectDeg(ctx, G, x0, x1, y1 - 0.7, y1, '#c0392b');
  textDeg(ctx, G, t(lang, 'home.calendar'), (x0 + x1) / 2, y1 - 0.5, 0.35, '#ffffff', 600, 'center');
  ctx.fillStyle = '#3a3f46';
  setFont(ctx, 0.17 * G.ppd, 500);
  ctx.textAlign = 'center';
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 7; c++) {
      const day = r * 7 + c - 1;
      if (day < 1 || day > 30) continue;
      ctx.fillStyle = c >= 5 ? '#c0392b' : '#3a3f46';
      ctx.fillText(String(day), G.X(x0 + 0.35 + c * 0.35), G.Y(y1 - 1.1 - r * 0.6));
    }
  }
  ctx.textAlign = 'left';
}

function drawMid(ctx: CanvasRenderingContext2D, lang: Lang): void {
  // Table top.
  const top = rectDeg(G, -20, 20, -12.2, -6);
  const wood = ctx.createLinearGradient(0, top.y, 0, top.y + top.h);
  wood.addColorStop(0, '#b07f4e');
  wood.addColorStop(1, '#8a5f36');
  ctx.fillStyle = wood;
  ctx.fillRect(top.x, top.y, top.w, top.h);
  ctx.strokeStyle = 'rgba(60, 35, 10, 0.18)';
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 9; i++) {
    const y = top.y + 14 + i * 20;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(RENDER_W * 0.3, y + 6, RENDER_W * 0.6, y - 6, RENDER_W, y + 3);
    ctx.stroke();
  }
  fillRectDeg(ctx, G, -20, 20, -6.15, -6, '#cfa572');

  drawLaptop(ctx, lang);
  drawTea(ctx);
}

function drawLaptop(ctx: CanvasRenderingContext2D, lang: Lang): void {
  // Base (keyboard deck) in perspective.
  polygonDeg(ctx, G, [[2.6, -9.6], [19.4, -9.6], [20, -12.2], [2, -12.2]]);
  ctx.fillStyle = '#4a4e55';
  ctx.fill();
  // Keys.
  ctx.fillStyle = '#31353b';
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 14; c++) {
      const y0 = -10.0 - r * 0.55;
      const x0 = 3.4 + c * 1.12 + r * 0.12;
      const rr = rectDeg(G, x0, x0 + 0.95, y0 - 0.42, y0);
      ctx.fillRect(rr.x, rr.y, rr.w, rr.h);
    }
  }
  // Screen with bezel.
  withShadow(ctx, 'rgba(0,0,0,0.3)', 14, 4, () => {
    fillRectDeg(ctx, G, 2.6, 19.4, -9.5, -1, '#23262b');
  });
  const s = rectDeg(G, 2.95, 19.05, -9.2, -1.3);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(s.x, s.y, s.w, s.h);
  // Web page: header bar, title, body.
  fillRectDeg(ctx, G, 2.95, 19.05, -1.9, -1.3, '#0e7c86');
  textDeg(ctx, G, 'linza.uz', 3.3, -1.75, 0.28, '#d8eef0', 600);
  textDeg(ctx, G, t(lang, 'home.laptop.title'), 3.4, -2.75, 0.4, '#1a2026', 700);
  const body = tl(lang, 'home.laptop.body');
  setFont(ctx, 0.26 * G.ppd, 400);
  ctx.fillStyle = '#2f3740';
  ctx.textAlign = 'left';
  let y = -3.45;
  const maxW = (19.05 - 3.4 - 0.4) * G.ppd;
  for (const para of body) {
    for (const line of wrapText(ctx, para, maxW)) {
      ctx.fillText(line, G.X(3.4), G.Y(y));
      y -= 0.5;
    }
    y -= 0.2;
    if (y < -8.9) break;
  }
}

function drawTea(ctx: CanvasRenderingContext2D): void {
  // Teapot body.
  withShadow(ctx, 'rgba(0,0,0,0.25)', 10, 4, () => {
    ctx.fillStyle = '#e4ebef';
    ellipseDeg(ctx, G, -4.2, -8.4, 1.7, 1.25);
    ctx.fill();
  });
  // Pattern band.
  ctx.save();
  ellipseDeg(ctx, G, -4.2, -8.4, 1.7, 1.25);
  ctx.clip();
  fillRectDeg(ctx, G, -6, -2.3, -8.8, -8.2, '#3b6fb6');
  ctx.fillStyle = '#e4ebef';
  for (let i = 0; i < 6; i++) {
    ellipseDeg(ctx, G, -5.6 + i * 0.55, -8.5, 0.12, 0.12);
    ctx.fill();
  }
  ctx.restore();
  // Spout and handle.
  ctx.strokeStyle = '#d3dce2';
  ctx.lineWidth = 0.32 * G.ppd;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(G.X(-2.9), G.Y(-8.3));
  ctx.quadraticCurveTo(G.X(-2.0), G.Y(-7.9), G.X(-1.9), G.Y(-7.0));
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(G.X(-5.6), G.Y(-8.0));
  ctx.quadraticCurveTo(G.X(-6.9), G.Y(-8.2), G.X(-5.9), G.Y(-9.2));
  ctx.stroke();
  // Lid.
  ctx.fillStyle = '#d3dce2';
  ellipseDeg(ctx, G, -4.2, -7.2, 0.8, 0.28);
  ctx.fill();
  ellipseDeg(ctx, G, -4.2, -7.0, 0.2, 0.2);
  ctx.fill();
  // Piyola (tea bowl).
  ctx.fillStyle = '#eef2f4';
  ctx.beginPath();
  ctx.ellipse(G.X(-0.9), G.Y(-9.1), 0.95 * G.ppd, 0.55 * G.ppd, 0, 0, Math.PI);
  ctx.fill();
  ctx.fillStyle = '#c28a3c';
  ellipseDeg(ctx, G, -0.9, -9.1, 0.9, 0.22);
  ctx.fill();
  ctx.strokeStyle = '#3b6fb6';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(G.X(-0.9), G.Y(-9.1), 0.95 * G.ppd, 0.55 * G.ppd, 0, 0.1, Math.PI - 0.1);
  ctx.stroke();
}

function drawNear(ctx: CanvasRenderingContext2D, lang: Lang): void {
  const x0 = -17.6;
  const x1 = -10.2;
  const y0 = -12.2;
  const y1 = 0.8;
  // Hand behind the phone: fingers and palm.
  ctx.fillStyle = '#e2b48f';
  for (let i = 0; i < 4; i++) {
    const r = rectDeg(G, x0 - 1.4, x0 + 0.6, y0 + 3 + i * 1.9, y0 + 4.6 + i * 1.9);
    roundRectPath(ctx, r.x, r.y, r.w, r.h, r.h / 2);
    ctx.fill();
  }
  polygonDeg(ctx, G, [[x0 - 1.2, y0], [x1 + 1.6, y0], [x1 + 1.6, y0 + 3.5], [x0 - 1.6, y0 + 7]]);
  ctx.fill();
  // Phone.
  withShadow(ctx, 'rgba(0,0,0,0.35)', 26, 10, () => {
    const r = rectDeg(G, x0, x1, y0 - 1, y1);
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 0.7 * G.ppd);
    ctx.fillStyle = '#101216';
    ctx.fill();
  });
  const s = rectDeg(G, x0 + 0.22, x1 - 0.22, y0 - 0.6, y1 - 0.22);
  roundRectPath(ctx, s.x, s.y, s.w, s.h, 0.5 * G.ppd);
  ctx.fillStyle = '#eef2f5';
  ctx.fill();
  // Top bar with time.
  ctx.save();
  roundRectPath(ctx, s.x, s.y, s.w, s.h, 0.5 * G.ppd);
  ctx.clip();
  fillRectDeg(ctx, G, x0, x1, y1 - 1.4, y1, '#0e7c86');
  ctx.restore();
  textDeg(ctx, G, '10:41', x0 + 0.5, y1 - 0.95, 0.26, '#ffffff', 600);
  textDeg(ctx, G, '●●● ▮', x1 - 0.5, y1 - 0.95, 0.22, '#ffffff', 400, 'right');
  // Messages.
  const msgs = tl(lang, 'home.phone.msgs');
  const cap = 0.26;
  setFont(ctx, cap * G.ppd, 400);
  const bubbleW = (x1 - x0 - 1.4) * G.ppd;
  let y = y1 - 2.0;
  msgs.forEach((msg, i) => {
    const mine = i % 2 === 1;
    const lines = wrapText(ctx, msg, bubbleW - 0.5 * G.ppd);
    const h = lines.length * 0.42 + 0.35;
    const bx0 = mine ? x1 - 0.5 - (x1 - x0 - 1.4) : x0 + 0.5;
    const bx1 = bx0 + (x1 - x0 - 1.4);
    const r = rectDeg(G, bx0, bx1, y - h, y);
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 0.3 * G.ppd);
    ctx.fillStyle = mine ? '#cdeee9' : '#ffffff';
    ctx.fill();
    ctx.fillStyle = '#1a2026';
    ctx.textAlign = 'left';
    lines.forEach((line, k) => {
      ctx.fillText(line, G.X(bx0 + 0.25), G.Y(y - 0.32 - k * 0.42));
    });
    y -= h + 0.3;
  });
  // Thumb over the right edge.
  ctx.fillStyle = '#e2b48f';
  ellipseDeg(ctx, G, x1 + 0.3, y0 + 3.2, 0.9, 1.9);
  ctx.fill();
}

export const homeScene: Scene = {
  id: 'home',
  hfov: HFOV,
  lighting: 'indoor',
  exposure: 1.0,
  layers: [
    { id: 'far', V: 0.25, draw: drawFar },
    { id: 'mid', V: 1.5, draw: drawMid },
    { id: 'near', V: 2.5, draw: drawNear },
  ],
  // The window glass is the glare source for cataract (SCENES §2): two sprites over the sky part of
  // the glass, so the tree and the building below stay visible.
  lights: () => [
    { layer: 'far', x: 8.85, y: 8.5, rx: 3.2, ry: 2.6, color: [0.92, 0.96, 1.0], intensity: 3.5 },
    { layer: 'far', x: 16.15, y: 8.5, rx: 3.2, ry: 2.6, color: [0.92, 0.96, 1.0], intensity: 3.5 },
  ],
  tags: [
    { x: -7.5, y: 10.1, key: 'tag.m4' },
    { x: 11, y: -0.5, key: 'tag.mid' },
    { x: -13.9, y: 1.4, key: 'tag.near' },
  ],
};
